<template>
  <label>
    <input
      class="input"
      type="checkbox"
      :id="id"
      :disabled="isDisabled"
      v-model="computedValue"
      :value="nativeValue"
    />
    <UiLabel
      :text="label"
      class="label"
      :class="['label-' + labelPosition]"
      :for="id"
      form="checkbox-form"
    />
  </label>
</template>

<script lang="ts" setup>
import UiLabel from '@sephora-asia/ui-label/UiLabel.vue'
import { watch, type WritableComputedRef } from 'vue'
import { LabelPositionOpts } from './types'
import { computed, ref } from 'vue'

interface CheckboxProps {
  id: string
  modelValue?: string[] | boolean // binding value of v-model; checkbox is checked by default if set to true
  label?: string
  ariaLabel?: string
  isDisabled?: boolean
  labelPosition?: LabelPositionOpts
  nativeValue?: string | boolean // boolean if native checkbox value is undefined
}

const emit = defineEmits(['update:modelValue'])
const props = withDefaults(defineProps<CheckboxProps>(), {
  modelValue: undefined,
  label: '',
  ariaLabel: '',
  labelPosition: LabelPositionOpts.RIGHT,
  isDisabled: false,
  nativeValue: undefined,
})
const newModelValue = ref(props.modelValue)
const computedValue: WritableComputedRef<string[] | boolean | undefined> =
  computed({
    get(): string[] | boolean | undefined {
      return newModelValue.value
    },
    set(value: string[] | boolean | undefined): void {
      if (typeof value === 'boolean') {
        newModelValue.value = value
      } else if (Array.isArray(value) && Array.isArray(newModelValue.value)) {
        if (value.length > newModelValue.value.length) {
          // adding the checked value
          newModelValue.value.push(value[value.length - 1])
        } else {
          // removing the unchecked value via differences between two arrays
          const difference = newModelValue.value.filter(
            modelValue => !value.includes(modelValue)
          )
          newModelValue.value.splice(
            newModelValue.value.indexOf(difference[0]),
            1
          )
        }
      }
      emit('update:modelValue', value)
    },
  })

// Update newModelValue if props.modelValue is resetted/updated directly in parent
watch(
  () => props.modelValue,
  (value: string[] | boolean | undefined) => {
    if (Array.isArray(value)) {
      newModelValue.value = value
    }
  }
)
</script>

<style lang="scss" scoped>
@mixin checkbox-base-style {
  content: '';
  border: 1px solid var(--color-base-grey-30);
  border-radius: var(--size-radius-low);
  background-color: var(--color-base-white);
  aspect-ratio: 1 / 1;
  height: 20px;
}

@mixin checkbox-checked {
  width: 5px;
  height: 10px;
  border: solid var(--color-base-white);
  border-width: 0 2px 2px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
  position: absolute;
  top: 3px;
}

label {
  user-select: none;
  display: flex;
  cursor: pointer;
  position: relative;

  &:has(input:disabled) {
    cursor: default;
  }

  input {
    position: absolute;
    visibility: none;
    height: 0;
    width: 0;
    &:checked ~ {
      .label {
        &-right {
          &::before {
            background-color: var(--color-base-black);
            border-color: var(--color-base-black);
          }
          &::after {
            content: '';
          }
        }
        &-left {
          &::before {
            content: '';
          }
          &::after {
            background-color: var(--color-base-black);
            border-color: var(--color-base-black);
          }
        }
      }
    }

    &:disabled ~ .label {
      color: var(--color-base-grey-30);
    }

    &:disabled:checked ~ {
      .label {
        &-right {
          &::before {
            opacity: 10%;
          }
          &::after {
            content: '';
          }
        }
        &-left {
          &::before {
            content: '';
          }
          &::after {
            opacity: 10%;
          }
        }
      }
    }

    &:disabled:not(:checked) ~ {
      .label {
        &-right {
          &::before {
            border: 1px solid var(--color-base-grey-10);
            background-color: var(--color-base-grey-05);
          }
        }
        &-left {
          &::after {
            border: 1px solid var(--color-base-grey-10);
            background-color: var(--color-base-grey-05);
          }
        }
      }
    }
  }
  .label {
    width: 100%;
    position: relative;
    &-right {
      &::before {
        @include checkbox-base-style;
        margin-right: 8px;
      }
      &::after {
        @include checkbox-checked;
        left: 7px;
      }
    }
    &-left {
      :deep(.text-container) {
        margin-right: 8px;
      }
      &::after {
        @include checkbox-base-style;
        margin-left: auto;
      }
      &::before {
        @include checkbox-checked;
        z-index: 1;
        right: 7px;
      }
    }
  }
}
</style>
